import React, { useEffect, useMemo, useRef } from 'react';
import { sanitize } from 'helpers/utils/htmlSanitize';
import { Log } from 'helpers/errorLogger';

export interface Props {
  markdown: string;
  className?: string;
  onCollectionLinkClick?: () => void;
}

const Markdown: React.FC<Props> = ({ markdown, className, onCollectionLinkClick }) => {
  const containerRef = useRef<HTMLDivElement | null>(null);

  const safeMarkdown = useMemo(
    () => (typeof markdown === 'string' ? sanitize(markdown, { ADD_ATTR: ['target'] }) : ''),
    [markdown],
  );

  useEffect(() => {
    const container = containerRef.current;

    if (!container) return;

    /*
     * Handle #collection custom links
     */
    const links = container.querySelectorAll('a');

    const handleCollectionLinkClick = (event: Event) => {
      event.preventDefault();

      if (onCollectionLinkClick) {
        onCollectionLinkClick();
      }
    };

    links.forEach((link) => {
      if (link.href.includes('#collection')) {
        link.addEventListener('click', handleCollectionLinkClick);
      }
    });

    /*
     * Handle accordion clicks
     */
    const handleAccordionClick = (event: MouseEvent) => {
      const target = event.target as HTMLElement | null;

      if (!target) return;

      const trigger = target.closest(
        '.riviere-accordion-header, .accordion-header, .accordion-title, .accordion-toggle, [data-accordion-toggle]',
      ) as HTMLElement | null;

      if (!trigger || !container.contains(trigger)) {
        return;
      }

      const parentAccordion =
        trigger.closest(
          '.riviere-accordion, .accordion, .accordion-item, accordian, accordion',
        ) || trigger.parentElement;

      if (!parentAccordion) {
        return;
      }

      const contentPanel =
        parentAccordion.querySelector(
          '.riviere-accordion-content, .accordion-content, .accordion-body',
        ) || trigger.nextElementSibling;

      if (!(contentPanel instanceof HTMLElement)) {
        return;
      }

      const icon = trigger.querySelector(
        '.riviere-accordion-icon, .accordion-icon',
      ) as HTMLElement | null;

      const isOpen = parentAccordion.classList.contains('active');

      event.preventDefault();

      if (isOpen) {
        /*
         * CLOSE
         */
        parentAccordion.classList.remove('active', 'open');

        trigger.setAttribute('aria-expanded', 'false');

        contentPanel.style.display = 'none';

        // Down arrow
        if (icon) {
          icon.style.transform = 'rotate(45deg)';
        }
      } else {
        /*
         * OPEN
         */
        parentAccordion.classList.add('active', 'open');

        trigger.setAttribute('aria-expanded', 'true');

        contentPanel.style.display = 'block';

        // Up arrow
        if (icon) {
          icon.style.transform = 'rotate(225deg)';
        }
      }
    };

    container.addEventListener('click', handleAccordionClick);

    /*
     * Cleanup
     */
    return () => {
      container.removeEventListener('click', handleAccordionClick);

      links.forEach((link) => {
        if (link.href.includes('#collection')) {
          link.removeEventListener('click', handleCollectionLinkClick);
        }
      });
    };
  }, [safeMarkdown, onCollectionLinkClick]);

  if (typeof markdown !== 'string') {
    Log.error(
      new Error(
        `Markdown: Invalid markdown property. Expected string but received ${typeof markdown}`,
      ),
    );

    return <></>;
  }

  return (
    <div
      ref={containerRef}
      dangerouslySetInnerHTML={{ __html: safeMarkdown }}
      className={className}
    />
  );
};

export default Markdown;
